import React, { useCallback, useEffect, useState } from 'react';

import { useFormik } from 'formik';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { templateDetail } from 'api/endpoints/template';
import Breadcrumb from 'components/Breadcrumb';
import Button from 'components/Button';
import LayoutReview from 'components/layouts/LayoutReview';
import { ENUM_QUESTION_TYPE, ENUM_SURVEY_STATUS, ENUM_SURVEY_TYPE, ENUM_TYPE_ACTION } from 'constants/enum';
import { TEXT_COMMON, TEXT_CREATE_SURVEY, TEXT_REPORT_LIST } from 'constants/string/texts';
import useTemplate from 'hooks/useTemplate';
import AppScreenReview from 'pages/Survey/_components/AppScreenReview';
import Form from 'pages/Survey/Form/Form';
import AppRoutes from 'routes/routes';
import { setLoading } from 'store/actions/common';
import { convertStatusSurvey, handleImageSurvey } from 'utils/helper';

const MoreVert = require('assets/resources/MoreVert.svg').default;

const SurveyTemplate = () => {
  const { id } = useParams();
  const navigator = useNavigate();
  const dispatch = useDispatch();
  const { makeDuplicateTemplate } = useTemplate();
  const { me } = useSelector((state) => state.auth);
  const [breadcrumbs, setBreadcrumbs] = useState([
    {
      title: TEXT_CREATE_SURVEY.SURVEY_LIST,
      event: () => {
        navigator(AppRoutes.survey);
      },
    },
  ]);

  const initialValues = (() => {
    const initData = {
      title: '',
      description: '',
      status: ENUM_SURVEY_STATUS.DRAFT,
      survey_type: ENUM_SURVEY_TYPE.EXAM,
      sections: [
        {
          title: '',
          order: 0,
          questions: [],
        },
      ],
    };

    return initData;
  })();

  const form = useFormik({
    initialValues: initialValues,
  });

  const handleImage = async (images) => {
    return await handleImageSurvey(images);
  };

  const handleQuestion = async (question) => {
    const processedImages = await handleImage(question.images);
    let options = [];
    if ([ENUM_QUESTION_TYPE.CHECKBOX, ENUM_QUESTION_TYPE.RADIO, ENUM_QUESTION_TYPE.DROPDOWN].includes(question.question_type)) {
      options = question.options.map((option) => ({ ...option, action: ENUM_TYPE_ACTION.UPDATE }));
    }

    return {
      ...question,
      action: ENUM_TYPE_ACTION.UPDATE,
      images: processedImages,
      options: options,
    };
  };

  const handleSection = async (section) => {
    const processedQuestions = [];
    for (const question of section.questions) {
      const questionHandled = await handleQuestion(question);
      processedQuestions.push(questionHandled);
    }

    return processedQuestions;
  };

  const handleSurvey = async (surveys) => {
    const dataSection = [];
    const sortedSections = _.sortBy(surveys.sections, 'order');
    for (const section of sortedSections) {
      const sectionHandled = await handleSection(section);
      dataSection.push({
        ...section,
        action: ENUM_TYPE_ACTION.UPDATE,
        questions: sectionHandled,
      });
    }

    for (const section of dataSection) {
      let questionCount = 0;
      for (const question of section.questions) {
        question.question_number = questionCount + 1;
        questionCount++;
      }
    }

    if (breadcrumbs.length === 1) {
      setBreadcrumbs([...breadcrumbs, { title: `${TEXT_COMMON.FV_TEMPLATE}${surveys.title}・${TEXT_REPORT_LIST.REPORT}` }]);
    }

    form.setValues({ ...surveys, sections: dataSection });
  };

  const getDetail = async () => {
    dispatch(setLoading({ isOpen: true }));
    const result = await templateDetail(id);
    if (result.data) {
      await handleSurvey(result.data);
    }

    dispatch(setLoading({ isOpen: false }));
  };

  const getSurveyDetail = useCallback(async () => {
    if (me) {
      await getDetail();
    }
  }, [me, id]);

  useEffect(() => {
    getSurveyDetail();
  }, [getSurveyDetail]);

  const publicSurvey = async () => {
    makeDuplicateTemplate(form.values.id, form.values);
  };

  return (
    <>
      <section id="create-survey" className="flex-middle-start gap-6 relative">
        <LayoutReview className="z-2">
          <form className="create-survey">
            <div id="header-form" className="flex-between-center mb-8">
              <div className="flex items-center gap-2">
                <div className="max-w-80 flex items-center gap-2">
                  <Breadcrumb breadcrumbs={breadcrumbs} />
                </div>
                <span
                  className={`text-xs rounded-sm flex-middle-center border border-solid min-w-13 min-h-5.75 ${convertStatusSurvey(form.values.status)?.class}`}
                >
                  {convertStatusSurvey(form.values.status).name}
                </span>
              </div>

              <div className="flex items-center gap-8 relative z-2">
                <div className="flex items-center gap-4">
                  <Button
                    onClick={publicSurvey}
                    type="button"
                    color="primary"
                    className="text-black/85 text-sm w-20 h-8"
                    text={TEXT_CREATE_SURVEY.SAVE_PUBLIC}
                  />

                  <div className="relative flex items-center">
                    <button type="button" className="w-6 h-8">
                      <img src={MoreVert} alt="more vert icon" width={24} height={24} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <Form form={form} viewOnly={true} showControlMenu={false} />
          </form>
        </LayoutReview>
        <div className="sticky top-header-padding">
          <AppScreenReview form={form} />
        </div>
      </section>
    </>
  );
};

export default React.memo(SurveyTemplate);
